import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EsignService } from '../../../services/esign-service/esign.service';
import { AuthService } from '../../../services/auth/auth.service';

/** @deprecated — kept for backward compat, no longer needed by parents */
export interface RechargePayload {
  amount: number;
  credits: number;
}

interface CreditPreset {
  credits: number;
  amount: number;
  label: string;
}

function multipleOfValidator(rate: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    if (!value || isNaN(value)) return null;
    return value % rate === 0 ? null : { multipleOf: { rate } };
  };
}

@Component({
  selector: 'app-add-credits-modal',
  standalone: false,
  templateUrl: './add-credits-modal.component.html',
  styleUrl: './add-credits-modal.component.scss',
})
export class AddCreditsModalComponent implements OnChanges, OnDestroy {

  @ViewChild('paymentModal') paymentModalTpl!: TemplateRef<any>;
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  readonly RATE = 15;
  private readonly PRESET_DEFS: { credits: number; label: string }[] = [
    { credits: 10, label: 'Starter' },
    { credits: 50, label: 'Pro' },
    { credits: 100, label: 'Business' },
  ];
  get presets(): CreditPreset[] {
    return this.PRESET_DEFS.map(p => ({ ...p, amount: p.credits * this.RATE }));
  }
  selectedPreset: CreditPreset | null = this.presets[1];
  form: FormGroup;
  isLoading = false;
  paymentUrl: SafeResourceUrl | null = null;
  paymentModalRef?: NgbModalRef;
  showSuccessToaster = false;
  showErrorToaster = false;
  successMessage = '';
  errorMessage = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly esignService: EsignService,
    private readonly sanitizer: DomSanitizer,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
  ) {
    this.form = this.fb.group({
      amount: [this.presets[1].amount, [Validators.required, Validators.min(this.RATE), multipleOfValidator(this.RATE)]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.selectedPreset = this.presets[1];
      this.form.patchValue({ amount: this.presets[1].amount });
    }
  }

  ngOnDestroy(): void {
    this.form.reset();
  }

  selectPreset(preset: CreditPreset): void {
    this.selectedPreset = preset;
    this.form.patchValue({ amount: preset.amount });
  }

  onCustomInput(): void {
    this.selectedPreset = null;
  }

  get amountValue(): number {
    return Number(this.form.get('amount')?.value) || 0;
  }

  get creditsPreview(): number {
    if (this.selectedPreset) return this.selectedPreset.credits;
    return Math.floor(this.amountValue / this.RATE);
  }

  get canSubmit(): boolean {
    return this.form.valid && this.creditsPreview > 0;
  }

  onRecharge(): void {
    if (!this.canSubmit || this.isLoading) return;
    this.isLoading = true;
    this.esignService.getWalletPaymentLink({ amount: this.amountValue }).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res?.success && res?.data?.short_url) {
          this.onClose();                              // close add-credits modal
          this.openPaymentIframe(res.data.short_url);  // open Razorpay iframe
        } else {
          this.errorMessage = 'Unable to generate payment link.';
          this.showErrorToaster = true;
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        this.errorMessage = err?.error?.error?.message || 'Failed to initiate payment.';
        this.showErrorToaster = true;
      },
    });
  }

  openPaymentIframe(url: string): void {
    this.paymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.paymentModalRef = this.modalService.open(this.paymentModalTpl, {
      backdrop: 'static',
      size: 'xl',
      centered: true,
    });
    this.paymentModalRef.result
      .then(() => { this.paymentUrl = null; this.refreshCredits(); })
      .catch(() => { this.paymentUrl = null; this.refreshCredits(); });
  }

  refreshCredits(): void {
    this.authService.triggerCreditsRefresh();
  }

  closePaymentModal(): void {
    this.paymentModalRef?.close();
    this.paymentModalRef = undefined;
    this.paymentUrl = null;
  }

  onClose(): void {
    this.form.reset({ amount: this.presets[1].amount });
    this.selectedPreset = this.presets[1];
    this.closeModal.emit();
  }

  onContentClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  closeToaster(type: 'success' | 'error'): void {
    if (type === 'success') { this.showSuccessToaster = false; this.successMessage = ''; }
    else { this.showErrorToaster = false; this.errorMessage = ''; }
  }
}
